import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useDescope } from '@descope/react-sdk';
import { Box, Button, Typography } from '@monorepo/nova-ui';
import { AppLoadingSpinner } from '@monorepo/react-components';
import { IDP_PROVIDER, IdpProviderKey } from '@monorepo/types';
import { useStore } from '../../helpers/use-store';
import { EnvService } from '../../services/core/env-service';
import UserStore from '../../stores/data/user/user-store';

/**
 * Component that handles Descope logout.
 * Only rendered when user logged in with Descope to avoid calling useDescope hook unnecessarily.
 */
const DescopeLogoutHandler: React.FC<{
  userStore: UserStore;
  onError: () => void;
}> = ({ userStore, onError }) => {
  const { logout: descopeLogout } = useDescope();

  useEffect(() => {
    const handleDescopeLogout = async () => {
      try {
        // Call Descope logout first (invalidates session and clears cookie)
        await descopeLogout();
      } catch (error) {
        console.warn('Descope logout error:', error);
        // Continue with backend logout even if Descope logout fails
      }

      // Always call backend logout (handles backend cleanup)
      try {
        await userStore.logout();
      } catch (error) {
        console.error('❌ Hub logout error:', error);
        onError();
      }
    };

    handleDescopeLogout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null; // This component doesn't render anything, it just handles logout
};

const LogoutPage: React.FC = () => {
  const {
    dataStore: { userStore },
  } = useStore();

  const [isError, setIsError] = useState(false);
  const { t } = useTranslation(['common']);

  // Check if user logged in with Descope (preserves logout behavior even if feature flag changes)
  const idpProvider =
    typeof window !== 'undefined' ? localStorage.getItem(IdpProviderKey) : null;
  const isDescopeUser = idpProvider === IDP_PROVIDER.DESCOPE;

  // Use Descope logout if user logged in with Descope
  // This allows users to properly logout even if feature flag is turned off
  const shouldUseDescopeLogout = isDescopeUser;

  useEffect(() => {
    // Only handle logout for non-Descope users here
    // Descope users are handled by DescopeLogoutHandler component
    // Early return prevents calling userStore.logout() twice
    if (shouldUseDescopeLogout) {
      return;
    }

    const handleLogout = async () => {
      try {
        await userStore.logout();
      } catch (error) {
        console.error('❌ Hub logout error:', error);
        setIsError(true);
      }
    };

    handleLogout();
  }, [userStore, shouldUseDescopeLogout]);

  if (isError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <Typography textAlign="center" variant="h6">
          {t('error.logout.error')}
        </Typography>
        <Button variant="outlined" size="md">
          <Link to="/">{t('error.logout.button')}</Link>
        </Button>
      </Box>
    );
  }

  return (
    <>
      {/* Only render Descope logout handler if user logged in with Descope */}
      {shouldUseDescopeLogout && (
        <DescopeLogoutHandler
          userStore={userStore}
          onError={() => setIsError(true)}
        />
      )}
      <AppLoadingSpinner application={EnvService.application} />
    </>
  );
};

export default LogoutPage;
